import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './YogaStylesFlipGrid.css';

// Import images
import vinyasaImage from './cardimages/flow.jpg';
import hathaImage from './cardimages/mountain.jpg';
import yinImage from './cardimages/moon.jpg';
import restorativeImage from './cardimages/nest.jpg';
import powerImage from './cardimages/fire.jpg';
import ashtangaImage from './cardimages/spiral.jpg';

const YogaStylesGrid = () => {
  const { t } = useTranslation();

  // Data array for yoga styles
  const yogaStyles = [
    {
      id: 1,
      name: t('yoga_styles.vinyasa.name', 'Vinyasa'),
      image: vinyasaImage,
      imageAlt: t('yoga_styles.vinyasa.image_alt', 'Flowing water representing Vinyasa yoga'),
      shortDescription: t('yoga_styles.vinyasa.short_description', 'A dynamic practice that synchronizes breath with movement, creating fluid sequences.'),
      fullDescription: t('yoga_styles.vinyasa.full_description', 'A dynamic practice that synchronizes breath with movement, creating fluid sequences. Vinyasa builds heat, strength, and flexibility while cultivating mindfulness through the breath. Classes feature Sun Salutations, continuous flow between postures, and creative sequencing that varies by teacher. Great for improving cardiovascular health and building upper body strength.'),
      emoji: '🌊'
    },
    {
      id: 2,
      name: t('yoga_styles.hatha.name', 'Hatha'),
      image: hathaImage,
      imageAlt: t('yoga_styles.hatha.image_alt', 'Mountain landscape representing Hatha yoga'),
      shortDescription: t('yoga_styles.hatha.short_description', 'The classic foundation of modern yoga balancing physical postures with breath control.'),
      fullDescription: t('yoga_styles.hatha.full_description', 'The classic foundation of modern yoga. Hatha balances physical postures (asanas) with breath control (pranayama) to harmonize opposing energies—"ha" (sun) and "tha" (moon). Features balanced standing and seated postures with longer holds to build strength, awareness, and proper alignment. Ideal for beginners and those seeking a solid foundation.'),
      emoji: '⛰️'
    },
    {
      id: 3,
      name: t('yoga_styles.yin.name', 'Yin'),
      image: yinImage,
      imageAlt: t('yoga_styles.yin.image_alt', 'Moon light representing Yin yoga'),
      shortDescription: t('yoga_styles.yin.short_description', 'A meditative, slow-paced practice where poses are held for 3-5 minutes or longer.'),
      fullDescription: t('yoga_styles.yin.full_description', 'A meditative, slow-paced practice where poses are held for 3-5 minutes or longer. Yin targets the deep connective tissues (fascia, ligaments, joints) rather than muscles, particularly in the hips, pelvis and lower spine. Passive poses with minimal muscular engagement create deep release. Excellent for increasing flexibility and joint mobility while promoting mindfulness.'),
      emoji: '🌙'
    },
    {
      id: 4,
      name: t('yoga_styles.restorative.name', 'Restorative'),
      image: restorativeImage,
      imageAlt: t('yoga_styles.restorative.image_alt', 'Cozy nest representing Restorative yoga'),
      shortDescription: t('yoga_styles.restorative.short_description', 'A gentle, therapeutic style that uses props to fully support the body in complete relaxation.'),
      fullDescription: t('yoga_styles.restorative.full_description', 'A gentle, therapeutic style that uses props to fully support the body in complete relaxation. Restorative yoga activates the parasympathetic nervous system ("rest and digest" mode), promoting deep healing. Sessions typically include only 4-6 poses held for 5-15 minutes each with extensive use of props. Perfect for stress reduction, improving sleep quality, and supporting recovery.'),
      emoji: '🧘‍♀️'
    },
    {
      id: 5,
      name: t('yoga_styles.power.name', 'Power'),
      image: powerImage,
      imageAlt: t('yoga_styles.power.image_alt', 'Fire representing Power yoga'),
      shortDescription: t('yoga_styles.power.short_description', 'An energetic, fitness-based approach that builds physical strength and endurance.'),
      fullDescription: t('yoga_styles.power.full_description', 'An energetic, fitness-based approach derived from Ashtanga. Power yoga builds physical strength, endurance, and flexibility through dynamic sequences with minimal breaks between poses. Features vigorous practice with core-strengthening exercises and challenging pose variations. Often practiced in heated rooms, it promotes detoxification through sweating and enhances athletic performance.'),
      emoji: '🔥'
    },
    {
      id: 6,
      name: t('yoga_styles.ashtanga.name', 'Ashtanga'),
      image: ashtangaImage, 
      imageAlt: t('yoga_styles.ashtanga.image_alt', 'Spiral representing Ashtanga yoga'),
      shortDescription: t('yoga_styles.ashtanga.short_description', 'A structured method following set sequences of increasing difficulty.'),
      fullDescription: t('yoga_styles.ashtanga.full_description', 'A structured method following six established series of increasing difficulty. Ashtanga links breath with movement through vinyasas (connecting movements) between poses, building internal heat. Practitioners follow the same sequence in the same order each time, incorporating Ujjayi breathing and energy locks (bandhas). Develops impressive strength, flexibility, discipline, and mental focus.'),
      emoji: '𖦹'
    },
  ];

  // State for tracking selections and matches
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDesc, setSelectedDesc] = useState(null);
  const [matches, setMatches] = useState([]);
  const [attempts, setAttempts] = useState(0);
  const [shuffledDescriptions, setShuffledDescriptions] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [allMatched, setAllMatched] = useState(false);

  // Shuffle descriptions when component mounts
  useEffect(() => {
    const shuffled = [...yogaStyles]
      .sort(() => Math.random() - 0.5)
      .map(style => ({
        id: style.id,
        shortDescription: style.shortDescription
      }));
      
    setShuffledDescriptions(shuffled);
  }, []);

  // Check if all items are matched
  useEffect(() => {
    if (matches.length === yogaStyles.length) {
      setAllMatched(true);
    }
  }, [matches]);

  // Handle type selection
  const handleTypeSelect = (id) => {
    if (matches.some(match => match.typeId === id)) return;
    
    setSelectedType(id);
  };

  // Handle description selection
  const handleDescSelect = (id) => {
    if (matches.some(match => match.descId === id)) return;
    
    setSelectedDesc(id);
  };

  // Check if match is correct
  useEffect(() => {
    if (selectedType !== null && selectedDesc !== null) {
      const isMatch = selectedType === selectedDesc;
      
      if (isMatch) {
        // Add to matches
        setMatches([...matches, { typeId: selectedType, descId: selectedDesc }]);
      }
      
      // Increment attempts
      setAttempts(attempts + 1);
      
      // Reset selections after a short delay
      setTimeout(() => {
        setSelectedType(null);
        setSelectedDesc(null);
      }, 500);
    }
  }, [selectedType, selectedDesc]);

  // Reset the game
  const resetGame = () => {
    setSelectedType(null);
    setSelectedDesc(null);
    setMatches([]);
    setAttempts(0);
    setShowResults(false);
    setAllMatched(false);
    
    // Reshuffle descriptions
    const shuffled = [...yogaStyles]
      .sort(() => Math.random() - 0.5)
      .map(style => ({
        id: style.id,
        shortDescription: style.shortDescription
      }));
      
    setShuffledDescriptions(shuffled);
  };

  // Show results modal
  const viewResults = () => {
    setShowResults(true);
  };

  return (
    <section className="yoga-styles-section">
      <h3 className="yoga-styles-heading">{t('yoga_styles.test_knowledge', 'Test Your Yoga Knowledge')}</h3>
      <p className="yoga-matching-instructions">
        {t('yoga_styles.matching_instructions', 'Match each yoga style with its correct description by clicking on a style, then clicking on the description you think matches it.')}
      </p>

      <div className="yoga-matching-container">
        <div className="yoga-types-column">
          <h4 className="column-title">{t('yoga_styles.column_styles', 'Yoga Styles')}</h4>
          <div className="yoga-types-list">
            {yogaStyles.map((style) => {
              const isMatched = matches.some(match => match.typeId === style.id);
              const isSelected = selectedType === style.id;
              
              return (
                <div 
                  key={style.id}
                  className={`yoga-type-item ${isMatched ? 'matched' : ''} ${isSelected ? 'selected' : ''}`}
                  onClick={() => !isMatched && handleTypeSelect(style.id)}
                >
                  <span className="yoga-type-emoji">{style.emoji}</span>
                  <span className="yoga-type-name">{style.name}</span>
                </div>
              );
            })}
          </div>
        </div>
        
        <div className="yoga-descriptions-column">
          <h4 className="column-title">{t('yoga_styles.column_descriptions', 'Descriptions')}</h4>
          <div className="yoga-descriptions-list">
            {shuffledDescriptions.map((desc) => {
              const isMatched = matches.some(match => match.descId === desc.id);
              const isSelected = selectedDesc === desc.id;
              
              return (
                <div 
                  key={desc.id}
                  className={`yoga-description-item ${isMatched ? 'matched' : ''} ${isSelected ? 'selected' : ''}`}
                  onClick={() => !isMatched && handleDescSelect(desc.id)}
                >
                  {desc.shortDescription}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      <div className="yoga-matching-stats">
        <span className="matches-counter">{t('yoga_styles.matches', 'Matches')}: {matches.length}/{yogaStyles.length}</span>
        <span className="attempts-counter">{t('yoga_styles.attempts', 'Attempts')}: {attempts}</span>
      </div>
      
      {allMatched && (
        <div className="matching-complete">
          <div className="complete-message">
            <h4>{t('yoga_styles.congratulations', 'Congratulations!')}</h4>
            <p>{t('yoga_styles.complete_message', 'You\'ve matched all yoga styles with their descriptions in {{attempts}} attempts!', { attempts })}</p>
            <div className="complete-buttons">
              <button className="result-button" onClick={viewResults}>{t('yoga_styles.view_details', 'View Details')}</button>
              <button className="reset-button" onClick={resetGame}>{t('yoga_styles.play_again', 'Play Again')}</button>
            </div>
          </div>
        </div>
      )}
      
      {showResults && (
        <div className="results-overlay" onClick={() => setShowResults(false)}>
          <div className="results-modal" onClick={e => e.stopPropagation()}>
            <h3 className="results-title">{t('yoga_styles.results_title', 'Your Yoga Knowledge Results')}</h3>
            <div className="results-content">
              <p className="results-summary">
                {t('yoga_styles.results_summary', 'You matched all {{count}} yoga styles in {{attempts}} attempts!', { count: yogaStyles.length, attempts })}
                {attempts === yogaStyles.length ? t('yoga_styles.perfect_score', ' Perfect score!') : ''}
              </p>
              <div className="yoga-style-details">
                {yogaStyles.map(style => (
                  <div key={style.id} className="yoga-detail-item">
                    <div className="yoga-detail-header">
                      <span className="yoga-detail-emoji">{style.emoji}</span>
                      <h4 className="yoga-detail-name">{style.name}</h4>
                    </div>
                    {/* <div className="yoga-detail-image">
                      <img src={style.image} alt={style.imageAlt} />
                    </div> */}
                    <p className="yoga-detail-description">{style.fullDescription}</p>
                  </div>
                ))}
              </div>
            </div>
            <button className="close-results-button" onClick={() => setShowResults(false)}>{t('close', 'Close')}</button>
          </div>
        </div>
      )}
    </section>
  );
};

export default YogaStylesGrid; 